import { Activity, CheckCircle, CreditCard, Loader, Lock, MinusCircle } from "react-feather"
import type { OwnerSummary200 } from "@/api/generated/models"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Card, CardContent, DateCard } from "@/components/ui/card"
import { Loading } from "@/components/ui/loading"

interface ServiceStatus {
  id: string
  label: string
  count: number
  total?: string
  icon: React.ComponentType<{ className?: string }>
  bgColor: string
  borderColor: string
  textColor: string
}

interface ServiceUsageSectionProps {
  summary?: OwnerSummary200
  isLoading?: boolean
}

export function ServiceUsageSection({ summary, isLoading }: ServiceUsageSectionProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-slate-400" />
          <h2 className="text-base font-semibold text-[#354267]">サービス利用状況</h2>
        </div>
        <Card className="shadow-sm">
          <CardContent className="p-5">
            <Loading className="py-10" />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!summary) {
    return null
  }

  const serviceStatuses: ServiceStatus[] = [
    {
      id: "active",
      label: "利用中",
      count: summary.roomsOfCurrentAndContinuePlan.length,
      total: `${summary.roomCount}戸中`,
      icon: CheckCircle,
      bgColor: "bg-green-50/30",
      borderColor: "border-[#25D2AF]",
      textColor: "text-[#25D2AF]",
    },
    {
      id: "pending",
      label: "開始予定",
      count: summary.roomsOfFuturePlan.length,
      icon: Loader,
      bgColor: "bg-blue-50/30",
      borderColor: "border-[#458CF7]",
      textColor: "text-[#458CF7]",
    },
    {
      id: "stopping",
      label: "停止予定",
      count: summary.roomsOfCurrentAndStopPlan.length,
      icon: MinusCircle,
      bgColor: "bg-red-50/30",
      borderColor: "border-[#FF3232]",
      textColor: "text-[#FF3232]",
    },
    {
      id: "inactive",
      label: "未利用",
      count: summary.roomsOfNoPlan.length,
      icon: Lock,
      bgColor: "bg-slate-100/60",
      borderColor: "border-slate-400",
      textColor: "text-slate-400",
    },
  ]

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Activity className="h-4 w-4 text-slate-400" />
        <h2 className="text-base font-semibold text-[#354267]">サービス利用状況</h2>
      </div>

      {/* Stats Overview */}
      <Card className="shadow-sm">
        <CardContent className="p-5">
          <div className="flex justify-between items-center">
            <div className="text-center flex-1">
              <p className="text-base font-semibold text-slate-500">総物件数</p>
              <p className="text-3xl font-bold text-[#354267] mt-1">
                {summary.propertyCount}
                <span className="text-xs text-slate-400 ml-1">棟</span>
              </p>
            </div>
            <div className="text-center flex-1">
              <p className="text-base font-semibold text-slate-500">総戸数</p>
              <p className="text-3xl font-bold text-[#354267] mt-1">
                {summary.roomCount}
                <span className="text-xs text-slate-400 ml-1">戸</span>
              </p>
            </div>
          </div>

          <div className="my-5 border-t border-slate-200" />

          {/* Status Cards */}
          <div className="grid grid-cols-1 gap-3">
            {serviceStatuses.map((status) => (
              <DateCard
                key={status.id}
                title={status.label}
                count={status.count.toString()}
                countText={status.total ? `件利用中 / ${status.total}` : "件"}
                icon={status.icon}
                bgColor={status.bgColor}
                borderColor={status.borderColor}
                textColor={status.textColor}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Total Amount Card */}
      <Card className="shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-600">合計金額</h3>
            <CreditCard className="h-5 w-5 text-slate-600" />
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-sm font-bold text-slate-600">今月</p>
              <p className="text-[22px] font-bold text-slate-600 mt-2">
                ¥{summary.invoice?.totalAmount?.toLocaleString() ?? 0}
              </p>
            </div>
          </div>

          <Alert className="bg-slate-100 border-slate-400 mt-4">
            <AlertDescription className="text-[#354267] font-semibold text-xs">
              表示金額は現時点のものであり、確定金額ではございません。
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  )
}
